import { useDispatch, useSelector } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ExternalIntegration } from 'dashboard/models/external-integration';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadExternalIntegrations } from 'dashboard/redux/thunk-actions/external-integrations';
import useApiLoader from 'lib/hooks/useApiLoader';
import { isPermissionsError } from 'lib/permissions-errors';

type UseExternalIntegrationHook = (
  externalIntegrationTypes: ExternalIntegration['type'][],
) => [ExternalIntegration[], { isLoading: boolean }];

const useExternalIntegration: UseExternalIntegrationHook = (
  externalIntegrationTypes: ExternalIntegration['type'][],
) => {
  const dispatch = useDispatch();

  const { loading: isLoading } = useApiLoader({
    loader: async () => {
      await dispatch(loadExternalIntegrations());
    },
    onNetworkError: () =>
      dispatch(
        showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
      ),
    onError: (err) => {
      if (isPermissionsError(err)) {
        dispatch(
          showMessage(
            'Your user role does not have permission to access connections.',
            MessageType.Notice,
          ),
        );
      } else {
        dispatch(showMessage('Failed to load connections, please try again.', MessageType.Failure));
      }
    },
    deps: [loadExternalIntegrations, showMessage],
  });

  const specifiedExternalIntegrations = useSelector<RootState, ExternalIntegration[]>(
    (state) => state.externalIntegrations.externalIntegrations,
  ).filter((int: ExternalIntegration) => externalIntegrationTypes.includes(int.type));

  return [specifiedExternalIntegrations, { isLoading }];
};

export default useExternalIntegration;
